import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@bookaburra/ui';
import { BrandLockup } from './BrandLockup.js';
import { APP_URL } from '../lib/urls.js';

/**
 * Sticky marketing-site header. Sage-tinted backdrop blur appears as soon
 * as the user scrolls; otherwise the header sits transparent on the
 * cream hero so the page feels edge-aligned.
 *
 * Mobile: nav links collapse into a hamburger that toggles a full-width
 * panel below the header bar.
 */
export function Header() {
  const [open, setOpen] = useState(false);

  // Close on Esc and on route change.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-brand-sage/10 bg-brand-cream/80 backdrop-blur supports-[backdrop-filter]:bg-brand-cream/60">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 sm:px-12">
        <BrandLockup />

        {/* Desktop nav — visible at sm+ */}
        <nav className="hidden items-center gap-1 sm:flex sm:gap-2">
          <NavLink to="/industries" label="Industries" />
          <NavLink to="/demos" label="Demos" />
          <NavLink to="/pricing" label="Pricing" />
          <NavLink to="/about" label="About" />
          <ExternalNavLink href={`${APP_URL}/signin`} label="Sign in" />
          <Button asChild className="ml-2">
            <a href={`${APP_URL}/signup`}>Start for free</a>
          </Button>
        </nav>

        {/* Mobile toggle — hidden at sm+ */}
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-ink-soft transition-colors hover:bg-brand-cream-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sage focus-visible:ring-offset-2 sm:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open ? (
        <nav className="border-t border-brand-sage/10 bg-brand-cream px-6 pb-5 pt-2 sm:hidden">
          <MobileLink to="/industries" label="Industries" onNavigate={() => setOpen(false)} />
          <MobileLink to="/demos" label="Demos" onNavigate={() => setOpen(false)} />
          <MobileLink to="/pricing" label="Pricing" onNavigate={() => setOpen(false)} />
          <MobileLink to="/about" label="About" onNavigate={() => setOpen(false)} />
          <MobileExternal href={`${APP_URL}/signin`} label="Sign in" />
          <div className="mt-3">
            <Button asChild size="lg" className="w-full">
              <a href={`${APP_URL}/signup`}>Start for free</a>
            </Button>
          </div>
        </nav>
      ) : null}
    </header>
  );
}

function NavLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="rounded-md px-3 py-2 text-sm font-medium text-brand-ink-soft/75 transition-colors hover:bg-brand-cream-shadow hover:text-brand-ink-soft"
      activeProps={{
        className: 'text-brand-ink-soft bg-brand-cream-shadow',
      }}
    >
      {label}
    </Link>
  );
}

/** Anchor variant for cross-subdomain links — TanStack Link only handles
 *  in-app routes. */
function ExternalNavLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="rounded-md px-3 py-2 text-sm font-medium text-brand-ink-soft/75 transition-colors hover:bg-brand-cream-shadow hover:text-brand-ink-soft"
    >
      {label}
    </a>
  );
}

function MobileLink({
  to,
  label,
  onNavigate,
}: {
  to: string;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="block rounded-md px-3 py-3 text-base font-medium text-brand-ink-soft/85 transition-colors hover:bg-brand-cream-shadow"
      activeProps={{
        className: 'text-brand-ink-soft bg-brand-cream-shadow',
      }}
    >
      {label}
    </Link>
  );
}

function MobileExternal({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="block rounded-md px-3 py-3 text-base font-medium text-brand-ink-soft/85 transition-colors hover:bg-brand-cream-shadow"
    >
      {label}
    </a>
  );
}

